import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Check, ArrowRight, Lock, Coins, Users, ArrowLeft, MessageCircle, Landmark, Copy } from "lucide-react";

/**
 * Pagamento Lega — Stripe Checkout (39€ one-time, EUR).
 * L'admin viene reindirizzato a Stripe. Al ritorno success → /pagamento/success (poll).
 */
export default function LeaguePayment() {
  const { leagueId } = useParams();
  const [league, setLeague] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [pricing, setPricing] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [reason, setReason] = useState("");
  const [feedbackSent, setFeedbackSent] = useState(false);
  const [searchParams] = useSearchParams();
  const nav = useNavigate();

  useEffect(() => {
    if (searchParams.get("cancelled") === "1") {
      toast.info("Pagamento annullato. Puoi riprovare quando vuoi.");
    }
    (async () => {
      try {
        const [rLg, rPr] = await Promise.all([
          api.get(`/leagues/${leagueId}`),
          api.get("/pricing/league").catch(() => ({ data: null })),
        ]);
        setLeague(rLg.data);
        setPricing(rPr.data);
        if (rLg.data.plan === "active" || rLg.data.plan === "founders") {
          nav(`/lega/${leagueId}`, { replace: true });
        }
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, [leagueId, nav, searchParams]);

  async function payWithStripe() {
    setPaying(true);
    api.post("/analytics/track", { event: "checkout_start", path: window.location.pathname }).catch(() => {});
    try {
      const { data } = await api.post(`/leagues/${leagueId}/create-checkout-session`, {
        origin_url: window.location.origin,
      });
      // Stripe Checkout rifiuta gli iframe (X-Frame-Options: DENY).
      // Se siamo dentro un iframe (es. preview Emergent), forziamo il top window;
      // in produzione questo è un no-op perché siamo già top.
      try {
        if (window.top && window.top !== window.self) {
          window.top.location.href = data.checkout_url;
          return;
        }
      } catch {
        // window.top può essere cross-origin bloccato; fallback su window.open
        window.open(data.checkout_url, "_top");
        return;
      }
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e));
      setPaying(false);
    }
  }

  async function sendFeedback(e) {
    e.preventDefault();
    if (!feedback.trim()) return;
    try {
      await api.post("/feedback", {
        league_id: leagueId,
        subject: "Non ho ancora attivato la lega",
        message: feedback.trim(),
        reason: reason || "not_paying_yet",
      });
      setFeedbackSent(true);
      toast.success("Grazie! Il team MyFantaBusiness leggerà il tuo messaggio.");
    } catch (err) { toast.error(formatApiError(err)); }
  }

  if (loading) return <FullScreenLoader />;
  if (!league) return <FullScreenLoader label="Lega non trovata" />;

  const budget = league.settings?.initial_budget ?? "?";
  const numTeams = league.settings?.num_teams ?? "?";

  return (
    <div className="min-h-screen bg-[#0b1120] text-white antialiased">
      <div className="fixed inset-0 pointer-events-none -z-10">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0b1120] via-[#0d1428] to-[#0b1120]" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-emerald-500/[0.08] rounded-full blur-[120px]" />
      </div>

      <nav className="border-b border-white/[0.06] backdrop-blur-xl bg-[#0b1120]/80 sticky top-0 z-30">
        <div className="max-w-3xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-slate-400 hover:text-white text-sm">
            <ArrowLeft size={16}/> Dashboard
          </Link>
          <div className="flex items-center gap-2 text-emerald-400 text-xs font-mono uppercase tracking-widest">
            <Lock size={12}/> Pagamento sicuro · Stripe
          </div>
        </div>
      </nav>

      <main className="max-w-lg mx-auto px-5 py-14">
        <div className="text-center mb-8">
          <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-emerald-400 mb-2">Ultimo passo</div>
          <h1 className="text-3xl sm:text-4xl font-black leading-tight">
            Attiva <span className="text-emerald-400">{league.name}</span> per la stagione.
          </h1>
          <p className="mt-3 text-slate-400 text-sm">Un unico pagamento. Copre l&apos;intera lega, tutti i partecipanti, tutta la stagione.</p>
        </div>

        <div className="rounded-3xl border border-emerald-500/30 bg-gradient-to-b from-emerald-500/[0.06] to-transparent p-7 shadow-[0_20px_60px_rgba(0,0,0,0.4)]">
          <div className="flex items-baseline gap-2 mb-2">
            <span className="text-6xl font-black text-white">{pricing?.amount_eur ?? 69}</span>
            <span className="text-2xl text-slate-400 font-bold">€</span>
            <span className="text-slate-500 text-sm ml-2">stagione completa</span>
          </div>
          {pricing?.is_discount ? (
            <div className="mb-5 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-amber-500/10 border border-amber-500/30">
              <span className="text-[10px] uppercase tracking-widest font-black text-amber-300">✨ Sconto applicato</span>
              <span className="text-xs text-slate-300">
                invece di <span className="line-through">{pricing.standard_eur} €</span> · {pricing.reason}
              </span>
            </div>
          ) : (
            <div className="mb-5 text-[11px] text-slate-500">Offerta lancio</div>
          )}

          <div className="grid grid-cols-2 gap-3 mb-5">
            <SummaryPill icon={<Coins size={14} className="text-amber-400"/>} label="Crediti asta" value={`${budget} cr`} />
            <SummaryPill icon={<Users size={14} className="text-sky-400"/>} label="Partecipanti" value={`${numTeams} squadre`} />
          </div>

          <div className="space-y-2 mb-6">
            <Feat text="Intera lega — tutti i manager"/>
            <Feat text="Rose permanenti, mercato, contratti, bilanci"/>
            <Feat text="3 chiamate AI gratuite per ogni manager"/>
            <Feat text="Storico e archivio permanenti"/>
            <Feat text="Nessun rinnovo automatico"/>
          </div>

          <button onClick={payWithStripe} disabled={paying}
            data-testid="pay-activate-btn"
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-[#0b1120] font-black text-base transition-all shadow-[0_10px_28px_rgba(16,185,129,0.45)] hover:-translate-y-0.5 disabled:opacity-60">
            {paying ? "Reindirizzamento a Stripe…" : (<>Paga {pricing?.amount_eur ?? 69} € con Stripe <ArrowRight size={18}/></>)}
          </button>
          <div className="mt-3 text-center text-[11px] text-slate-500 flex items-center justify-center gap-1.5">
            <Lock size={11}/> Elaborato da Stripe · Carta test 4242 4242 4242 4242
          </div>

          {/* Fallback bonifico — visibile in piccolo sotto il tasto paga */}
          <BonificoFallback
            leagueId={leagueId}
            leagueName={league.name}
            amount={pricing?.amount_eur ?? 69}
          />
        </div>

        <p className="mt-6 text-center text-[11px] text-slate-500">
          In una lega da {numTeams} manager sono circa{" "}
          <span className="text-white font-bold">
            {Math.round((pricing?.amount_eur ?? 69) / (numTeams || 10) * 100) / 100} €
          </span>{" "}
          a persona.
        </p>

        {/* Feedback form for non-paying admins */}
        <div className="mt-10 pt-8 border-t border-white/[0.06]">
          {!showFeedback ? (
            <button
              onClick={() => setShowFeedback(true)}
              data-testid="pay-open-feedback"
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg border border-slate-700 hover:border-amber-500 hover:bg-amber-500/5 text-slate-300 hover:text-amber-300 text-sm transition-colors"
            >
              <MessageCircle size={14}/> Non vuoi procedere ora? Scrivici il motivo
            </button>
          ) : feedbackSent ? (
            <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-5 text-center">
              <div className="text-emerald-300 font-bold mb-1">Grazie!</div>
              <div className="text-sm text-slate-400">
                Il team MyFantaBusiness leggerà il tuo messaggio. Ti contatteremo se serve chiarire qualcosa.
              </div>
            </div>
          ) : (
            <form onSubmit={sendFeedback} className="rounded-xl border border-white/10 bg-white/[0.02] p-5 space-y-3" data-testid="pay-feedback-form">
              <div className="text-sm font-bold text-slate-200 flex items-center gap-2">
                <MessageCircle size={14} className="text-amber-400"/> Aiutaci a capire
              </div>
              <p className="text-xs text-slate-400">
                Cosa ti sta trattenendo? Il tuo feedback arriva direttamente a MyFantaBusiness.
              </p>
              <select
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                data-testid="pay-feedback-reason"
                className="w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm"
              >
                <option value="">Seleziona un motivo (opzionale)</option>
                <option value="price_too_high">Il prezzo è troppo alto</option>
                <option value="need_more_info">Voglio più informazioni prima di pagare</option>
                <option value="waiting_group">Aspetto conferma dagli altri manager</option>
                <option value="testing_features">Sto ancora testando le funzionalità</option>
                <option value="payment_issue">Ho un problema con Stripe/pagamento</option>
                <option value="other">Altro</option>
              </select>
              <textarea
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                data-testid="pay-feedback-message"
                rows={4}
                required
                placeholder="Scrivici in libertà — leggiamo tutti i messaggi."
                className="w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm resize-none focus:border-amber-500 outline-none"
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setShowFeedback(false)}
                  className="px-4 py-2 rounded-md border border-slate-700 hover:bg-slate-800 text-slate-300 text-sm"
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  data-testid="pay-feedback-send"
                  disabled={!feedback.trim()}
                  className="flex-1 px-4 py-2 rounded-md bg-amber-500 hover:bg-amber-400 text-slate-950 text-sm font-bold disabled:opacity-50"
                >
                  Invia a MyFantaBusiness
                </button>
              </div>
            </form>
          )}
        </div>
      </main>
    </div>
  );
}

function SummaryPill({ icon, label, value }) {
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3">
      <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1">
        {icon} {label}
      </div>
      <div className="text-white font-bold font-mono">{value}</div>
    </div>
  );
}

function Feat({ text }) {
  return (
    <div className="flex items-center gap-2.5 text-sm">
      <div className="w-4 h-4 rounded-full bg-emerald-500 flex items-center justify-center shrink-0">
        <Check size={10} className="text-[#0b1120]" strokeWidth={3.5}/>
      </div>
      <span className="text-slate-200">{text}</span>
    </div>
  );
}

function FullScreenLoader({ label = "Caricamento…" }) {
  return (
    <div className="min-h-screen bg-[#0b1120] text-slate-400 flex items-center justify-center text-sm">
      {label}
    </div>
  );
}

function BonificoFallback({ leagueId, leagueName, amount }) {
  const [open, setOpen] = useState(false);
  const IBAN = "IT06M0103003371000001773651";
  const INTESTATARIO = "Giampaolo Castellani";
  const causale = `MyFantaBusiness ${leagueName || leagueId} - ${leagueId}`;

  const copy = async (val, label) => {
    try {
      await navigator.clipboard.writeText(val);
      toast.success(`${label} copiato`);
    } catch {
      toast.error("Impossibile copiare");
    }
  };

  return (
    <div className="mt-4 pt-4 border-t border-white/[0.06]" data-testid="pay-bonifico-fallback">
      {!open ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="pay-bonifico-toggle"
          className="w-full inline-flex items-center justify-center gap-1.5 text-[11px] text-slate-500 hover:text-slate-300 py-1.5 transition-colors"
        >
          <Landmark size={11}/> Problemi con la carta? Paga anche con bonifico bancario
        </button>
      ) : (
        <div className="rounded-lg border border-slate-700/60 bg-slate-950/40 p-3 text-[11px] leading-relaxed space-y-2" data-testid="pay-bonifico-details">
          <div className="flex items-center gap-1.5 text-slate-300 font-bold text-xs">
            <Landmark size={12} className="text-sky-400"/> Bonifico bancario
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="ml-auto text-slate-500 hover:text-slate-300 text-[10px] uppercase tracking-widest"
            >Chiudi</button>
          </div>
          <p className="text-slate-400 text-[11px]">
            In attesa che risolviamo un problema temporaneo con Stripe, puoi attivare la lega anche via bonifico.
            Riceverai conferma manuale entro 24h dal ricevimento.
          </p>
          <dl className="grid grid-cols-[80px_1fr] gap-x-3 gap-y-1.5 text-slate-300 font-mono">
            <dt className="text-slate-500">Intestatario</dt>
            <dd className="flex items-center gap-1.5 truncate" data-testid="pay-bonifico-intestatario">
              <span className="truncate">{INTESTATARIO}</span>
              <button type="button" onClick={() => copy(INTESTATARIO, "Intestatario")} className="text-slate-500 hover:text-sky-400 shrink-0" title="Copia">
                <Copy size={11}/>
              </button>
            </dd>
            <dt className="text-slate-500">IBAN</dt>
            <dd className="flex items-center gap-1.5 truncate" data-testid="pay-bonifico-iban">
              <span className="truncate select-all">{IBAN}</span>
              <button type="button" onClick={() => copy(IBAN, "IBAN")} className="text-slate-500 hover:text-sky-400 shrink-0" title="Copia IBAN">
                <Copy size={11}/>
              </button>
            </dd>
            <dt className="text-slate-500">Importo</dt>
            <dd className="text-emerald-300 font-bold" data-testid="pay-bonifico-amount">{amount} €</dd>
            <dt className="text-slate-500">Causale</dt>
            <dd className="flex items-center gap-1.5 truncate" data-testid="pay-bonifico-causale">
              <span className="truncate text-slate-200">{causale}</span>
              <button type="button" onClick={() => copy(causale, "Causale")} className="text-slate-500 hover:text-sky-400 shrink-0" title="Copia causale">
                <Copy size={11}/>
              </button>
            </dd>
          </dl>
          <div className="text-[10px] text-amber-300/80 pt-1">
            Una volta effettuato il bonifico, inviaci la ricevuta a{" "}
            <a href="mailto:myfantabusiness@gmail.com?subject=Ricevuta bonifico lega" className="underline hover:text-amber-200">
              myfantabusiness@gmail.com
            </a>{" "}
            citando il codice lega <span className="font-mono text-amber-200">{leagueId}</span>.
          </div>
        </div>
      )}
    </div>
  );
}
